import { aggregateUseCase } from "../../../../usecases/object";
import { _toISOString } from "../../helper/dateHelper";
import { buildTwentyFourSevenPipeline } from "./TwentyFourSevenPipeline";

export const fetchEmployeeTwentyFourSeven = async (employeeId, maxDateStr, cache) => {
  if (!employeeId || !maxDateStr) return [];

  let assignments = cache.byEmployee.get(employeeId);

  if (!assignments) {
    try {
      const pipeline = buildTwentyFourSevenPipeline(employeeId, maxDateStr);
      const results = await aggregateUseCase().execute("twenty_fourSeven_assignment", pipeline);

      assignments = results.map((assignment) => ({
        ...assignment,
        createdDateStr: assignment.created ? _toISOString(new Date(assignment.created)) : "",
      }));

      cache.byEmployee.set(employeeId, assignments);
    } catch (error) {
      console.error(`Error fetching twenty_fourSeven_assignment for employee ${employeeId}:`, error);
      cache.byEmployee.set(employeeId, []);
      return [];
    }
  } else {
    const latestCachedDate = assignments.length > 0
        ? (assignments[0].createdDateStr || null)
        : null;

    if (latestCachedDate && maxDateStr > latestCachedDate) {
      try {
        const pipeline = buildTwentyFourSevenPipeline(employeeId, maxDateStr, latestCachedDate);
        const results = await aggregateUseCase().execute("twenty_fourSeven_assignment", pipeline);

        const newAssignments = results.map((assignment) => ({
            ...assignment,
            createdDateStr: assignment.created ? _toISOString(new Date(assignment.created)) : "",
          }),
        );

        assignments = [...newAssignments, ...assignments].sort((a, b) =>
          b.createdDateStr.localeCompare(a.createdDateStr),
        );
        cache.byEmployee.set(employeeId, assignments);
      } catch (error) {
        console.error(`Error fetching updated twenty_fourSeven_assignment for employee ${employeeId}:`, error);
      }
    }
  }

  return assignments;
};


const createdDateStringExpr = {
  $cond: {
    if: { $eq: [{ $type: "$created" }, "date"] },
    then: {
      $dateToString: {
        date: "$created",
        format: "%Y-%m-%d",
      },
    },
    else: {
      $cond: {
        if: { $eq: [{ $type: "$created" }, "string"] },
        then: { $substr: ["$created", 0, 10] },
        else: { $toString: "$created" },
      },
    },
  },
};

/**
 * One aggregate per chunk of employeeIds (vs one per employee).
 * Match/filter semantics align with buildTwentyFourSevenPipeline (created <= maxDateStr).
 */
export const fetchEmployeeTwentyFourSevenBatch = async (
  employeeIds,
  maxDateStr,
  cache,
  batchSize,
) => {
  if (!cache?.byEmployee) cache.byEmployee = new Map();

  const toFetch = employeeIds.filter((id) => !cache.byEmployee.has(id));
  if (!toFetch.length) {
    return cache.byEmployee;
  }

  const buildBatchPipeline = (batch) => [
    {
      $match: {
        $expr: {
          $and: [
            { $in: ["$employeeId", batch] },
            { $lte: [createdDateStringExpr, maxDateStr] },
          ],
        },
      },
    },
    {
      $addFields: {
        createdDate: {
          $cond: {
            if: { $eq: [{ $type: "$created" }, "date"] },
            then: "$created",
            else: {
              $cond: {
                if: { $eq: [{ $type: "$created" }, "string"] },
                then: { $toDate: "$created" },
                else: null,
              },
            },
          },
        },
      },
    },
    { $sort: { employeeId: 1, createdDate: -1 } },
    {
      $project: {
        employeeId: 1,
        name: 1,
        created: 1,
      },
    },
  ];

  for (let i = 0; i < toFetch.length; i += batchSize) {
    const batch = toFetch.slice(i, i + batchSize);
    try {
      for (const empId of batch) {
        cache.byEmployee.set(empId, []);
      }

      const pipeline = buildBatchPipeline(batch);
      const results = await aggregateUseCase().execute(
        "twenty_fourSeven_assignment",
        pipeline,
      );

      for (const doc of results || []) {
        const empId = doc?.employeeId;
        if (!empId) continue;
        if (!cache.byEmployee.has(empId)) continue;
        cache.byEmployee.get(empId).push({
          ...doc,
          createdDateStr: doc.created ? _toISOString(new Date(doc.created)) : "",
        });
      }
    } catch (error) {
      console.error("Error fetching twenty_fourSeven_assignment batch:", error);
      for (const empId of batch) {
        cache.byEmployee.set(empId, []);
      }
    }
  }

  return cache.byEmployee;
};
